import React, { useCallback, useState } from 'react';
import { InventoryEntry, contentInventoryService } from '../../../../services/contentInventoryService';

const PILLAR_OPTIONS = [
  'Cluster 1: Competitive Positioning and Buyer Guides',
  'Cluster 2: Cross-Cultural Workplace Communication',
  'Cluster 3: Support and CX Team Communication',
  'Cluster 4: HR and L&D Strategy',
  'Cluster 5: Meetings and Presentations',
  'Cluster 6: Professional Email and Business Writing',
  'Cluster 7: Speaking with Confidence',
  'Cluster 8: Business English for Specific Functions',
  'Cluster 9: Unscripted Moments',
  'Cluster 10: Grammar and Vocabulary',
  'Cluster 11: Leadership',
];

const FUNNEL_OPTIONS = ['top', 'mid', 'bottom', 'authority'];

// ---------------------------------------------------------------------------
// Article Detail View
// ---------------------------------------------------------------------------

export function ContentInventoryDetail({ entry, onBack, onEntryUpdated }: { entry: InventoryEntry; onBack: () => void; onEntryUpdated?: (url: string, enrichment: any) => void }) {
  const enrichment = entry.enrichment;
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleFieldChange = useCallback(async (field: 'content_pillar' | 'funnel_stage', value: string) => {
    setSaving(field);
    setSaveError(null);
    try {
      const result = await contentInventoryService.updateEnrichment(entry.url, { [field]: value });
      if (onEntryUpdated) onEntryUpdated(entry.url, result.enrichment);
    } catch (e: any) {
      setSaveError(e?.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  }, [entry.url, onEntryUpdated]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-3 inline-block">
          &larr; Back to inventory
        </button>
        <h2 className="text-lg font-semibold text-gray-900">{entry.title}</h2>
        <a href={entry.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 break-all">
          {entry.url}
        </a>
      </div>

      {/* Basic Info */}
      <div className="bg-white border rounded p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Article Info</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-400 block text-xs">Type</span>
            <span className="text-gray-700 uppercase">{entry.content_type}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Author</span>
            <span className="text-gray-700">{entry.author || '—'}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Category</span>
            <span className="text-gray-700">{entry.category || '—'}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Word Count</span>
            <span className="text-gray-700">{entry.word_count?.toLocaleString() || '—'}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">WordPress ID</span>
            <span className="text-gray-700 font-mono">{entry.wp_id}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Published</span>
            <span className="text-gray-700">{entry.published_at ? new Date(entry.published_at).toLocaleDateString() : '—'}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Modified</span>
            <span className="text-gray-700">{entry.modified_at ? new Date(entry.modified_at).toLocaleDateString() : '—'}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Slug</span>
            <span className="text-gray-700 font-mono text-xs">{entry.slug}</span>
          </div>
          <div>
            <span className="text-gray-400 block text-xs">Last Synced</span>
            <span className="text-gray-700">{entry.synced_at ? new Date(entry.synced_at).toLocaleString() : '—'}</span>
          </div>
        </div>
      </div>

      {/* Excerpt */}
      {entry.excerpt && (
        <div className="bg-white border rounded p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Excerpt</h3>
          <p className="text-sm text-gray-600">{entry.excerpt}</p>
        </div>
      )}

      {/* Enrichment */}
      {enrichment ? (
        <div className="bg-white border rounded p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Enrichment
            </h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
              Source: {enrichment.source}
            </span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-4">
            <div>
              <span className="text-gray-400 block text-xs">Primary Topic</span>
              <span className="text-gray-700 font-medium">{enrichment.primary_topic}</span>
            </div>
            <div>
              <span className="text-gray-400 block text-xs">ICP</span>
              <span className="text-gray-700">{enrichment.icp}</span>
            </div>
            <div>
              <span className="text-gray-400 block text-xs mb-0.5">Funnel Stage</span>
              <select
                value={enrichment.funnel_stage || ''}
                onChange={(e) => handleFieldChange('funnel_stage', e.target.value)}
                disabled={saving === 'funnel_stage'}
                className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700 bg-white capitalize w-full"
              >
                <option value="">—</option>
                {FUNNEL_OPTIONS.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              {saving === 'funnel_stage' && <span className="text-[10px] text-gray-400">Saving...</span>}
            </div>
            <div>
              <span className="text-gray-400 block text-xs mb-0.5">Category</span>
              <select
                value={enrichment.content_pillar || ''}
                onChange={(e) => handleFieldChange('content_pillar', e.target.value)}
                disabled={saving === 'content_pillar'}
                className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700 bg-white w-full"
              >
                <option value="">—</option>
                {PILLAR_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              {saving === 'content_pillar' && <span className="text-[10px] text-gray-400">Saving...</span>}
            </div>
            {saveError && (
              <div className="col-span-2 lg:col-span-3 text-xs text-red-600">{saveError}</div>
            )}
          </div>

          <div className="mb-4">
            <span className="text-gray-400 block text-xs mb-1">Summary</span>
            <p className="text-sm text-gray-600">{enrichment.summary}</p>
          </div>

          <div>
            <span className="text-gray-400 block text-xs mb-2">Keywords</span>
            <div className="flex flex-wrap gap-1.5">
              {(enrichment.keywords || []).map((kw) => (
                <span key={kw} className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                  {kw}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border rounded p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Enrichment</h3>
          <p className="text-sm text-gray-400 italic">Not yet enriched. Run a sync to enrich this article.</p>
        </div>
      )}

      {/* Pipeline Data */}
      {entry.pipeline && (
        <div className="bg-white border rounded p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Pipeline Data</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-400 block text-xs">Primary Keyword</span>
              <span className="text-gray-700 font-medium">{entry.pipeline.primary_keyword}</span>
            </div>
            {entry.pipeline.seo_score != null && (
              <div>
                <span className="text-gray-400 block text-xs">SEO Score</span>
                <span className="text-gray-700">{entry.pipeline.seo_score}</span>
              </div>
            )}
            <div>
              <span className="text-gray-400 block text-xs">Candidate ID</span>
              <span className="text-gray-700 font-mono text-xs">{entry.pipeline.candidate_id}</span>
            </div>
          </div>
          {entry.pipeline.secondary_keywords?.length > 0 && (
            <div className="mt-3">
              <span className="text-gray-400 block text-xs mb-1">Secondary Keywords</span>
              <div className="flex flex-wrap gap-1.5">
                {entry.pipeline.secondary_keywords.map((kw) => (
                  <span key={kw} className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700">{kw}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Headings */}
      {entry.headings && entry.headings.length > 0 && (
        <div className="bg-white border rounded p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Headings ({entry.headings.length})
          </h3>
          <ul className="space-y-1">
            {entry.headings.map((h, i) => (
              <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                <span className="text-gray-300 font-mono text-xs mt-0.5">{i + 1}</span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Metrics placeholder */}
      <div className="bg-white border rounded p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Metrics</h3>
        <p className="text-sm text-gray-400 italic">Coming in Phase 2 (Google Search Console integration)</p>
      </div>
    </div>
  );
}
